import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Select,
  MenuItem,
  OutlinedInput,
  CircularProgress,
  InputLabel,
  FormControl,
  Box,
  Switch
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import {
  ResponsiveContainer,
  YAxis,
  XAxis,
  Tooltip,
  Legend,
  Bar,
  BarChart,
  LineChart, Line, CartesianGrid, Text
} from "recharts";

// components
import PageTitle from "../../components/PageTitle";
import { Typography } from "../../components/Wrappers";
import CustomizedYaxisLabel from "../../components/Chart/CustomizedYaxisLabel";
import MultiSelectAutoComplete from '../../components/MultiSelectAutoComplete/MultiSelectAutoComplete';

import classnames from "classnames";

import ttAPI from '../../api/ttAPI';
import { useUserState } from "../../context/UserContext";

// styles
import useStyles from "./styles";
import Widget from "../../components/Widget";


import './chart.css'

export default function Insights(props) {
  var { user_name, fakeLogin } = useUserState();
  var classes = useStyles();
  var theme = useTheme();


  // star rate, tag count and tag rate
  var [dataView, setDataView] = useState('Star Rate');
  var [companyView, setCompanyView] = useState('ALL');
  var [regionalView, setRegionalView] = useState('NONE');
  var [viewOption, setViewOption] = useState(0);
  //var [showCompanyData, setShowCompanyData] = useState(true);
  var [regionsOptions, setRegionsOptions] = useState([]);
  var [selectedRegions, setSelectedRegions] = useState([]);
  var [technicianOptions, setTechnicianOptions] = useState([]);
  var [selectedTechnicians, setSelectedTechnicians] = useState([]);
  var [personalInsights, setPersonalInsights] = useState([]);
  var [regionalInsights, setRegionalInsights] = useState([]);
  var [companyInsights, setCompanyInsights] = useState([]);

  const prevSelectedTechnicians = usePrevious(selectedTechnicians);
  const prevSelectedRegions = usePrevious(selectedRegions);

  // for view technicians as regional or as individual (same data source)
  var [viewTechnicians, setViewTechnicians] = useState(false);
  // for view technicians as individual
  var [viewIndividual, setViewIndividual] = useState(false);


  //var [isMonthly, setIsMonthly] = useState([]);
  const regionWarmColours = [null,'#EF5350','#EC407A', '#FFA726', '#FF7043', '#E57373', '#F06292','#D32F2F','#FFB74D',null, null, null, null, null, null, null,'#FFCA28','#AB47BC'];
  const regionColdColours = [null,'#5C6BC0','#42A5F5', '#29B6F6', '#1E88E5', '#66BB6A', '#9CCC65','#81C784','#AED581',null, null, null, null, null, null, null,'#DCE775','#26C6DA'];

  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

  const warmColours = [
    '#FF5733', '#FF6E4A', '#FF854F', '#FF9C66', '#FFB377', '#FFC488', '#FFD599', '#FFBBAD', '#FFADAD', '#FFC1AD',
    '#FF6033', '#FF7D4A', '#FF8F4F', '#FFA366', '#FFB677', '#FFC788', '#FFD399', '#FFADAD', '#FFBEAD', '#FFC7AD',
    '#FF6933', '#FF814A', '#FF964F', '#FFAE66', '#FFBB77', '#FFCA88', '#FFD999', '#FFC1AD', '#FFB7AD', '#FFCDAD',
    '#FF7333', '#FF884A', '#FF9B4F', '#FFB166', '#FFC277', '#FFD388', '#FFE099', '#FFBEAD', '#FFB7AD', '#FFC2AD',
    '#FF7D33', '#FF934A', '#FFA64F', '#FFB966', '#FFC577', '#FFD688', '#FFE499', '#FFC1AD', '#FFBEAD', '#FFC4AD'
  ];

  const coldColours = [
    '#102027', '#1B2631', '#27374D', '#34495E', '#3B4D5F', '#455A64', '#4A6572', '#546E7A', '#607D8B', '#68757B',
    '#73868C', '#78909C', '#7D8C8C', '#8395A7', '#90A4AE', '#95A5A6', '#9E9E9E', '#AAB7B8', '#B0BEC5', '#BFC9CA',
    '#7F8C8D', '#4D5656', '#708090', '#2E4053', '#556B2F', '#006400', '#008000', '#00FF00', '#2E8B57', '#8FBC8F',
    '#20B2AA', '#5F9EA0', '#4682B4', '#B0E0E6', '#87CEFA', '#6495ED', '#4169E1', '#1E90FF', '#2C3E50', '#34495E',
    '#5D6D7E', '#85929E', '#AEB6BF', '#8395A7', '#7F8C8D', '#95A5A6', '#BDC3C7', '#384A5A', '#AEB5AF', '#ABB2B9'
  ];

  let usedWarmColors = new Map();
  let usedColdColors = new Map();

  const getTechnicianColorIndex = (id, isWarm) => {
      if (isWarm ? usedWarmColors.has(id) : usedColdColors.has(id)) {
          return isWarm ? usedWarmColors.get(id) : usedColdColors.get(id);
      } else {
          const colorIndex = (isWarm ? usedWarmColors.size : usedColdColors.size) % (isWarm ? warmColours.length : coldColours.length);
          if (isWarm) {
              usedWarmColors.set(id, colorIndex);
          }
          else {
              usedColdColors.set(id, colorIndex);
          }
          return colorIndex;
      }
  };


  const [leftYAxisLabelName, setLeftYAxisLabelName] = useState('Star Rate');
  const rightYAxisLabelName = 'Productivity';

  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [isChartRendered, setIsChartRendered] = useState(false);

  // Custom hook to get the previous value of a state or prop
  function usePrevious(value) {
      const ref = React.useRef();
      React.useEffect(() => {
          ref.current = value;
      });
      return ref.current;
  }

  // Custom function to deep compare objects or arrays
  function isEqual(a, b) {
      return JSON.stringify(a) === JSON.stringify(b);
  }

  useEffect(() => {
      setLeftYAxisLabelName(dataView);
  },[dataView]);

  useEffect(() => {
      if (dataView !== companyView && companyView !== 'Productivity' &&
          companyView !== 'ALL' && companyView !== 'NONE') {
          setCompanyView(dataView);
      }
  },[dataView, companyView]);


  useEffect(() => {
    if (chartRef.current && isChartRendered) {
      const { width, height } = chartRef.current.getBoundingClientRect();
      //console.log('set chart width', width);
      setChartWidth(width);
    }

  }, [isChartRendered]);

  useEffect(() => {
      if (!viewTechnicians) {
        usedWarmColors = new Map();
        usedColdColors = new Map();
      }
  }, [viewTechnicians]);

  useEffect(() => {
    let mounted = true;
    const handleResize = () => {
      if (chartRef.current) {
        const width = chartRef.current.clientWidth;
        setChartWidth(width);
      }
    };

    const fetchRegions = async() => {
        const response = await ttAPI.getRegions();
        if(mounted){
            if (response !== null && response !== undefined) {
                const regions = response.filter(option => option.name.includes('Test and Tag'));
                setRegionsOptions(regions);
                //setSelectedRegions(regions.map(value => value.id));
                setSelectedRegions(regions);

            }
        }
     }

     const fetchTechnicians = async() => {
         const response = await ttAPI.getAllAccounts();
         if(mounted){
             if (response !== null && response !== undefined) {
                 const techs = response.filter(option => (
                   option.active && !option.account_access && !option.franchise_access && !option.customer_access &&
                   option.customer_id === null &&
                   option.franchise_name.includes('Test and Tag')
                 ));
                 setTechnicianOptions(techs);
                 setSelectedTechnicians(techs);
             }
         }
      }

        const fetchCompanyInsights = async() => {
            const response = await ttAPI.getInsights('company');
            if(mounted){
                if (response !== null && response !== undefined) {
                    setCompanyInsights(response);
                }
            }
         }

    handleResize();
    window.addEventListener('resize', handleResize);

    if (regionsOptions === null || regionsOptions === undefined || regionsOptions.length <= 0) {
        fetchRegions();
    }

    if (technicianOptions === null || technicianOptions === undefined || technicianOptions.length <= 0) {
        fetchTechnicians();
    }




    if (companyInsights === null || companyInsights === undefined || companyInsights.length <= 0) {
        fetchCompanyInsights();
    }


    return () => {
      mounted = false;
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchRegionalInsights = async() => {
        const response = await ttAPI.getInsights('regional');
        if(mounted){
            if (response !== null && response !== undefined) {
              const insightsWithIds = response.map(insight => {
                  const region = regionsOptions.find(region => region.name === insight.region);
                  if (region) {
                      return { ...insight, id: region.id, name: region.name };
                  } else {
                      return insight;
                  }
              });
                setRegionalInsights(insightsWithIds);


            }
        }
     }

    if (regionsOptions !== null && regionsOptions !== undefined && regionsOptions.length > 0) {
        fetchRegionalInsights();
    }

    return () => {
      mounted = false;

    };
  },[regionsOptions]);

  useEffect(() => {
    let mounted = true;

    const fetchPersonalInsights = async() => {
        const response = await ttAPI.getInsights('personal');
        if(mounted){
            if (response !== null && response !== undefined) {
              const insightsWithIds = response.map(insight => {
                  //const person = technicianOptions.find(tech => tech.name === insight.name );
                  const person = technicianOptions.find(tech => {
                      if (!viewTechnicians) {
                          return tech.name === insight.name;
                      } else {
                          if (viewIndividual) {
                              return tech.name === insight.name;
                          }
                          else {
                              return tech.name === insight.name && selectedRegions.some(region => region.name === tech.franchise_name);
                          }
                      }
                  });

                  if (person) {
                      return { ...insight, id: person.id, name: person.name };
                  } else {
                      return insight;
                  }
              });
                setPersonalInsights(insightsWithIds);
            }
        }
     }

     if (personalInsights === null || personalInsights === undefined || personalInsights.length <= 0) {
         fetchPersonalInsights();
     }

    return () => {
      mounted = false;

    };
  },[technicianOptions, regionsOptions, viewTechnicians, viewIndividual, selectedRegions]);

  const alignedData = React.useMemo(() => {
    if (regionalInsights !== undefined && regionalInsights !== null && regionalInsights.length > 0 &&
        personalInsights !== undefined && personalInsights !== null && personalInsights.length > 0) {
        // get mon_year from region is faster and easier
        const uniqueMonYears = [...new Set(regionalInsights.map(item => item.mon_year))];
        const ad = uniqueMonYears.map(monYear => {
            const dataForMonYear = (viewTechnicians ? personalInsights : regionalInsights).filter(item => item.mon_year === monYear);
            if (viewTechnicians) {
                const dataForMonYearByTechnician = {};
                const techKeys = dataForMonYear
                    .filter(item => {
                        if (viewIndividual) {
                            return selectedTechnicians.some(tech => tech.name === item.name);
                        }
                        else {
                          // Check if both selectedRegions and selectedTechnicians are empty
                          if (selectedRegions.length === 0 && selectedTechnicians.length === 0) {
                            return false; // If both are empty, return false to filter out all items
                          }

                          // Filter based on selectedRegions and selectedTechnicians if both are non-empty
                          if (selectedRegions.length > 0 && selectedTechnicians.length > 0) {
                            return (
                              selectedRegions.some(region => region.name === item.region) &&
                              selectedTechnicians.some(tech => tech.name === item.name && tech.franchise_name === item.region)
                            );
                          }

                          // Filter based on selectedRegions if it's non-empty
                          if (selectedRegions.length > 0) {
                            return selectedRegions.some(region => region.name === item.region);
                          }

                          // Filter based on selectedTechnicians if it's non-empty
                          if (selectedTechnicians.length > 0) {
                            return selectedTechnicians.some(tech => tech.name === item.name && tech.franchise_name === item.region);
                          }

                          return false; // Default case: return false to filter out all items
                        }

                    })
                    .map(item => item.id);
                dataForMonYear.forEach(item => {
                     if (techKeys.includes(item.id)) {
                        dataForMonYearByTechnician[item.id] = {
                            name: item.name,
                            region: item.region,
                            star_rate: item.star_rate,
                            tag_rate: item.tag_rate,
                            tag_count: item.tag_count,
                            productivity_ratio: item.productivity_ratio
                        };
                     }
                });
                return {
                    mon_year: monYear,
                    ...dataForMonYearByTechnician
                };
            }
            else {
                const dataForMonYearByRegion = {};
                const regionKeys = dataForMonYear
                         .filter(item => selectedRegions.some(region => region.id === item.id))
                         .map(item => item.id);
                dataForMonYear.forEach(item => {
                     if (regionKeys.includes(item.id)) {
                        dataForMonYearByRegion[item.id] = {
                            name: item.region,
                            star_rate: item.star_rate,
                            tag_rate: item.tag_rate,
                            tag_count: item.tag_count,
                            productivity_ratio: item.productivity_ratio
                        };
                     }
                });

                return {
                    mon_year: monYear,
                    ...dataForMonYearByRegion
                };
            }

        });
        //console.log(ad);
        return ad;
    }

  },[regionalInsights, selectedRegions, personalInsights, selectedTechnicians, viewTechnicians, viewIndividual]);

  const companyData = React.useMemo(() => {
    if (companyInsights !== undefined && companyInsights !== null && companyInsights.length > 0) {
        // get mon_year from region is faster and easier
        const uniqueMonYears = [...new Set(companyInsights.map(item => item.mon_year))];
        const ad = uniqueMonYears.map(monYear => {
            const dataForMonYear = companyInsights.filter(item => item.mon_year === monYear);

                const dataForMonYearByCompany = {};
                dataForMonYear.forEach(item => {
                    //console.log(item);
                      dataForMonYearByCompany['company'] = {
                          name: 'COMPANY',
                          star_rate: item.star_rate,
                          tag_rate: item.tag_rate,
                          tag_count: item.tag_count,
                          productivity_ratio: item.productivity_ratio
                      };

                });

                return {
                    mon_year: monYear,
                    ...dataForMonYearByCompany
                };


        });
        //console.log(ad);
        return ad;
    }

  },[companyInsights]);

  const regionalData = React.useMemo(() => {
    if (regionalInsights !== undefined && regionalInsights !== null && regionalInsights.length > 0) {
        const uniqueMonYears = [...new Set(regionalInsights.map(item => item.mon_year))];
        const ad = uniqueMonYears.map(monYear => {
            const dataForMonYear = regionalInsights.filter(item => item.mon_year === monYear);

                const dataForMonYearByRegion= {};
                dataForMonYear.forEach(item => {
                    //console.log(item);
                      dataForMonYearByRegion[item.id] = {
                          name: item.name,
                          star_rate: item.star_rate,
                          tag_rate: item.tag_rate,
                          tag_count: item.tag_count,
                          productivity_ratio: item.productivity_ratio
                      };

                });

                return {
                    mon_year: monYear,
                    ...dataForMonYearByRegion
                };


        });
        return ad;
    }

  },[regionalInsights]);

  const combinedData = React.useMemo(() => {
      if (alignedData !== undefined && alignedData !== null &&
          companyData !== undefined && companyData !== null &&
          regionalData !== undefined && regionalData !== null) {
        let newData = alignedData.map(item => ({
            ...item,
            company: companyData.find(companyItem => companyItem.mon_year === item.mon_year)?.company || null
          }));

        if (viewOption == 1) {
            newData = newData.map(item => {
                const newDataItem = { ...item };
                selectedRegions.forEach(region => {
                  const { id } = region;
                  newDataItem[`region${id}`] = regionalData.find(regionItem => regionItem.mon_year === item.mon_year)?.[id] || null;
                });
                return newDataItem;
            });
        }
        return newData;
      }

},[dataView, regionalView, companyView, alignedData, companyData, regionalData]);

  React.useEffect(() => {

    if (viewTechnicians) {
        let filteredTechnicians = selectedTechnicians;
        let filteredRegions = selectedRegions;

        if (viewIndividual) {
            // nothing to do?
        }
        else {
            if (selectedTechnicians.length === 0 && selectedRegions.length === 0) {
                // If both are empty, clear all both filters
                filteredTechnicians = [];
                filteredRegions = [];
            }
            // if both have something selected
            else if (selectedRegions.length > 0 && selectedTechnicians.length > 0) {
              let isTechnicianSelectionChanged = !isEqual(prevSelectedTechnicians, selectedTechnicians);

              if (isTechnicianSelectionChanged) {
                  filteredRegions = regionsOptions.filter(region =>
                      selectedTechnicians.some(tech => region.name === tech.franchise_name)
                  );
              }
              else {
                  filteredTechnicians = technicianOptions.filter(tech =>
                      selectedRegions.some(region => region.name === tech.franchise_name)
                  );
              }
            }
            else if (selectedRegions.length > 0) {
                // Filter based on selectedRegions if it's non-empty
                filteredTechnicians = technicianOptions.filter(tech =>
                    selectedRegions.some(region => region.name === tech.franchise_name)
                );
            }
            else if (selectedTechnicians.length > 0) {
                // Filter based on selectedTechnicians if it's non-empty
                filteredRegions = regionsOptions.filter(region =>
                    selectedTechnicians.some(tech => region.name === tech.franchise_name)
                );
            }
        }


        // Check if the filtered arrays are different from the current state before updating
        if (!arraysAreEqual(filteredTechnicians, selectedTechnicians)) {
            setSelectedTechnicians(filteredTechnicians);
        }
        if (!arraysAreEqual(filteredRegions, selectedRegions)) {
            setSelectedRegions(filteredRegions);
        }

    }

  }, [selectedTechnicians, selectedRegions, viewTechnicians]);

  // Function to check if two arrays are equal
  function arraysAreEqual(arr1, arr2) {
      if (arr1.length !== arr2.length) return false;
      for (let i = 0; i < arr1.length; i++) {
          if (arr1[i] !== arr2[i]) return false;
      }
      return true;
  }

  const legendItems = React.useMemo(() => {
      if (selectedRegions !== undefined && selectedRegions !== null &&
        selectedTechnicians !== undefined && selectedTechnicians !== null) {
        const items = [];

        if (viewTechnicians) {
            if (selectedTechnicians.length <= 10) {
                selectedTechnicians.forEach(tech => {
                  const { name, id } = tech;
                  items.push({
                    value: `${name} ${dataView}`,
                    color: warmColours[getTechnicianColorIndex(id, true)],
                  });
                  items.push({
                    value: `${name} Productivity`,
                    color: coldColours[getTechnicianColorIndex(id, false)],
                  });
                });
            }

        }
        else {
          selectedRegions.forEach(region => {
            const { name, id } = region;
            items.push({
              value: `${name} ${dataView}`,
              color: regionWarmColours[id],
            });
            items.push({
              value: `${name} Productivity`,
              color: regionColdColours[id],
            });
          });
        }
          return items;
      }
  }, [selectedRegions, selectedTechnicians, viewTechnicians, dataView, companyView, regionalView]);

  useEffect(() => {
    if (user_name && alignedData !== undefined && alignedData &&
        companyData !== undefined && companyData &&
        regionalData !== undefined && regionalData &&
        combinedData !== undefined && combinedData) {
      setIsChartRendered(true);
    }
  }, [user_name, alignedData, companyData, regionalData, combinedData]);


  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const keys = Object.keys(data).filter(key => key !== 'mon_year'); // Exclude mon_year from keys
      const catData = keys.map(key => ({ name: key, ...data[key] }));

      return (
        <div className={classes.chartTooltip}>
          <p className={classes.chartTooltipLabel}>{label}</p>
          <div className={classes.chartTooltipTable}>
            <div className={classes.chartTooltipRow}>
              <div className={classes.chartTooltipCellHeader} style={{ width: '250px' }}>&nbsp;&nbsp;&nbsp;&nbsp;</div>
              <div className={classes.chartTooltipCellHeader}>Star Rate</div>
              <div className={classes.chartTooltipCellHeader}>Tag Rate</div>
              <div className={classes.chartTooltipCellHeader}>Tag Count</div>
              <div className={classes.chartTooltipCellHeader}>Productivity</div>
            </div>
            {catData.map(cd => {
              const isCompany = cd.name === 'COMPANY';
              const isRegion = !isCompany && cd.name.includes('Test and Tag');
              let id = 0;
              if (isCompany) {
                id = 0;
              }
              else if (isRegion) {
                id = regionsOptions.find(region => region.name === cd.name)?.id;
              }
              else {
                id = technicianOptions.find(tech => tech.name === cd.name)?.id;
              }

              return (
                <div key={cd.name ?? 'COMPANY'} className={classes.chartTooltipRow}>
                  <div className={classes.chartTooltipCell}>{cd.name}</div>
                  <div className={classes.chartTooltipCell} style={{ fontWeight: isCompany ? 'bold' : 'normal', color: isCompany ? '#B76E79' : isRegion ? regionWarmColours[id] : warmColours[getTechnicianColorIndex(id, true)]}}>{cd.star_rate}</div>
                  <div className={classes.chartTooltipCell} style={{ fontWeight: isCompany ? 'bold' : 'normal', color: isCompany ? '#B76E79' : isRegion ? regionWarmColours[id] : warmColours[getTechnicianColorIndex(id, true)]}}>{cd.tag_rate}</div>
                  <div className={classes.chartTooltipCell} style={{ fontWeight: isCompany ? 'bold' : 'normal', color: isCompany ? '#B76E79' : isRegion ? regionWarmColours[id] : warmColours[getTechnicianColorIndex(id, true)]}}>{cd.tag_count}</div>
                  <div className={classes.chartTooltipCell} style={{ fontWeight: isCompany ? 'bold' : 'normal', color: isCompany ? '#5f9ea0' : isRegion ? regionColdColours[id] : coldColours[getTechnicianColorIndex(id, false)]}}>{cd.productivity_ratio}</div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };


  function onSelectRegions(values) {
      //setSelectedRegions(values.map(({ id }) => id));
      setSelectedRegions(values);
      if (values.length === 0) {
          setSelectedTechnicians([]);
      }
  }

  function onSelectTechnicians(values) {
      //setSelectedTechnicians(values.map(({ id }) => id));
      setSelectedTechnicians(values);
      if (values.length === 0) {
          setSelectedRegions([]);
      }
  }

  /*function onClickViewTechniciansSwitch() {
      setViewTechnicians(!viewTechnicians);
  }

  function onClickViewIndividualSwitch() {
      setViewIndividual(!viewIndividual);
  }

  function onClickshowCompanyDataSwitch() {
      setShowCompanyData(!showCompanyData)
  }*/

  const generateCompanyLine = (key, dataKey, stroke, yAxisId, name) => (
    <Line
      key={key}
      type="monotone"
      dataKey={dataKey}
      stroke={stroke}
      strokeWidth={5}
      yAxisId={yAxisId}
      name={name}
    />
  );

  const generateRegionalLine = (key, dataKey, stroke, yAxisId, name) => (
    <Line
      key={key}
      type="monotone"
      dataKey={dataKey}
      stroke={stroke}
      strokeWidth={3}
      yAxisId={yAxisId}
      name={name}
    />
  );

  const renderCompanyLine = () => {
      switch (companyView) {
        case 'Star Rate':
          return generateCompanyLine(
            "company-line-1",
            "company.star_rate",
            "#B76E79", // rose Gold color
            "left",
            `COMPANY ${companyView}`
          );
        case 'Tag Rate':
          return generateCompanyLine(
            "company-line-1",
            "company.tag_rate",
            "#B76E79", // rose Gold color
            "left",
            `COMPANY ${companyView}`
          );
        case 'Tag Count':
          return generateCompanyLine(
            "company-line-1",
            "company.tag_count",
            "#B76E79", // rose Gold color
            "left",
            `COMPANY ${companyView}`
          );
        case 'Productivity':
          return generateCompanyLine(
            "company-line-2",
            "company.productivity_ratio",
            "#5f9ea0", // golden blue?
            "right",
            `COMPANY Productivity`
          );
        case 'ALL':
          return [
            generateCompanyLine(
              "company-line-1",
              dataView === 'Star Rate' ? `company.star_rate` : (dataView === 'Tag Rate' ? `company.tag_rate` : `company.tag_count`),
              //"company.star_rate",
              "#B76E79", // rose Gold color
              "left",
              `COMPANY ${companyView}`
            ),
            generateCompanyLine(
              "company-line-2",
              "company.productivity_ratio",
              "#5f9ea0", // golden blue?
              "right",
              `COMPANY Productivity`
            )
          ];
        default:
          return null;
      }
    };

    const renderRegionalLine = () => {
        const lines = [];
        selectedRegions.forEach(region => {
            switch (regionalView) {
              case 'Star Rate':
                lines.push(generateRegionalLine(
                  `region-line-${region.id}`,
                  `region${region.id}.star_rate`,
                  regionWarmColours[region.id],
                  "left",
                  `${region.name} Star Rate`
                ));
                break;
              case 'Tag Rate':
                lines.push(generateRegionalLine(
                  `region-line-${region.id}`,
                  `region${region.id}.tag_rate`,
                  regionWarmColours[region.id],
                  "left",
                  `${region.name} Tag Rate`
                ));
                break;
              case 'Tag Count':
                lines.push(generateRegionalLine(
                  `region-line-${region.id}`,
                  `region${region.id}.tag_count`,
                  regionWarmColours[region.id],
                  "left",
                  `${region.name} Tag Count`
                ));
                break;
              case 'Productivity':
                lines.push(generateRegionalLine(
                  `region-line-${region.id}`,
                  `region${region.id}.productivity_ratio`,
                  regionColdColours[region.id],
                  "right",
                  `${region.name} Productivity`
                ));
                break;
              case 'ALL':
                lines.push(
                  generateRegionalLine(
                    `region-line-${region.id}-1`,
                    dataView === 'Star Rate' ? `region${region.id}.star_rate` : (dataView === 'Tag Rate' ? `region${region.id}.tag_rate` : `region${region.id}.tag_count`),
                    regionWarmColours[region.id],
                    "left",
                    `${region.name} ${dataView}`
                  ));
                lines.push(
                  generateRegionalLine(
                    `region-line-${region.id}-2`,
                    `region${region.id}.productivity_ratio`,
                    regionColdColours[region.id],
                    "right",
                    `${region.name} Productivity`
                  )
                );
                break;
              default:
                break;
            }
        });
        return lines;
      };


  if (fakeLogin) {
    return (<><CircularProgress size={26}/></>);
  }
  return (user_name && combinedData !== undefined && combinedData !== null && combinedData.length > 0 &&
          regionalInsights !== undefined && regionalInsights !== null && regionalInsights.length > 0 &&
          regionsOptions !== undefined && regionsOptions !== null && regionsOptions.length > 0 &&
          personalInsights !== undefined && personalInsights !== null && personalInsights.length > 0 &&
          companyInsights !== undefined && companyInsights !== null && companyInsights.length > 0 &&
          technicianOptions !== undefined && technicianOptions !== null && technicianOptions.length > 0 &&
    <>
        <PageTitle title="Insights"/>
        <Grid item xs={12}>
          <Widget
            bodyClass={classes.mainChartBody}
            header={
              <div className={classes.mainChartHeader}>
                <Typography
                  variant="h6"
                  color="text"
                  weight={"medium"}
                  colorBrightness="secondary"
                >

                </Typography>

                <div className={classes.filters}>

                    <div className={classnames({
                      [classes.formControl]: true,
                      [classes.filterCenter]: true,
                    })}>
                    <FormControl>
                    <InputLabel>View as</InputLabel>
                      <Select
                        value={viewOption}
                        onChange={e => {
                            setViewOption(e.target.value);
                            setViewTechnicians(e.target.value == 1 || e.target.value == 2);
                            setViewIndividual(e.target.value == 2);
                          }}
                        input={
                          <OutlinedInput
                            labelWidth={0}
                            classes={{
                              notchedOutline: classes.mainChartSelectRoot,
                              input: classes.mainChartSelect
                            }}
                          />
                        }
                        autoWidth
                        className={classes.filter}
                      >
                        <MenuItem value="0">Regional Overview</MenuItem>
                        <MenuItem value="1">Regional Technician Overview</MenuItem>
                        <MenuItem value="2">Individual Technician Overview</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl>
                    <InputLabel>Data View as</InputLabel>
                      <Select
                        value={dataView}
                        onChange={e => setDataView(e.target.value)}
                        input={
                          <OutlinedInput
                            labelWidth={0}
                            classes={{
                              notchedOutline: classes.mainChartSelectRoot,
                              input: classes.mainChartSelect
                            }}
                          />
                        }
                        autoWidth
                        className={classes.filter}
                      >
                        <MenuItem value="Star Rate">Star Rate</MenuItem>
                        <MenuItem value="Tag Rate">Tag Rate</MenuItem>
                        <MenuItem value="Tag Count">Tag Count</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl style={{width: '160px'}}>
                    <InputLabel>Show Company Data</InputLabel>
                      <Select
                        value={companyView}
                        onChange={e => setCompanyView(e.target.value)}
                        input={
                          <OutlinedInput
                            labelWidth={0}
                            classes={{
                              notchedOutline: classes.mainChartSelectRoot,
                              input: classes.mainChartSelect
                            }}
                          />
                        }
                        autoWidth
                        className={classes.filter}
                      >
                        <MenuItem value="ALL">ALL</MenuItem>
                        <MenuItem value="NONE">NONE</MenuItem>
                        { dataView === 'Star Rate' && <MenuItem value="Star Rate">Star Rate</MenuItem>}
                        { dataView === 'Tag Rate' && <MenuItem value="Tag Rate">Tag Rate</MenuItem>}
                        { dataView === 'Tag Count' && <MenuItem value="Tag Count">Tag Count</MenuItem>}
                        <MenuItem value="Productivity">Productivity</MenuItem>
                      </Select>
                    </FormControl>

                    {viewOption == 1 && <FormControl style={{width: '160px'}}>
                    <InputLabel>Show Regional Data</InputLabel>
                      <Select
                        value={regionalView}
                        onChange={e => setRegionalView(e.target.value)}
                        input={
                          <OutlinedInput
                            labelWidth={0}
                            classes={{
                              notchedOutline: classes.mainChartSelectRoot,
                              input: classes.mainChartSelect
                            }}
                          />
                        }
                        autoWidth
                        className={classes.filter}
                      >
                        <MenuItem value="ALL">ALL</MenuItem>
                        <MenuItem value="NONE">NONE</MenuItem>
                        { dataView === 'Star Rate' && <MenuItem value="Star Rate">Star Rate</MenuItem>}
                        { dataView === 'Tag Rate' && <MenuItem value="Tag Rate">Tag Rate</MenuItem>}
                        { dataView === 'Tag Count' && <MenuItem value="Tag Count">Tag Count</MenuItem>}
                        <MenuItem value="Productivity">Productivity</MenuItem>
                      </Select>
                    </FormControl>}
                    </div>

                    {!viewIndividual && <FormControl className={classnames({
                      [classes.formControl]: true,
                      [classes.filterRight]: true,
                    })}>
                      {regionsOptions !== null && regionsOptions !== undefined &&
                          regionsOptions.length > 0 &&
                          <div style={{    width: 'auto', margin: 'auto', minWidth: '300px'}}>
                            <MultiSelectAutoComplete options={regionsOptions}
                                                   label="Regions"
                                                   idName="id"
                                                   displayName="name"
                                                   presetValue={selectedRegions}
                                                   onSelect={onSelectRegions}/>
                          </div>}

                    </FormControl>}

                    {viewTechnicians && <FormControl className={classnames({
                      [classes.formControl]: true,
                      [classes.formRight]: true,
                    })}>
                      {technicianOptions !== null && technicianOptions !== undefined &&
                          technicianOptions.length > 0 &&
                          <div style={{    width: 'auto', margin: 'auto', minWidth: '300px'}}>
                            <MultiSelectAutoComplete
                                                   options={technicianOptions}
                                                   label="Technicians"
                                                   idName="id"
                                                   displayName="name"
                                                   presetValue={selectedTechnicians}
                                                   onSelect={onSelectTechnicians}/>
                          </div>}
                    </FormControl>}

                  </div>
              </div>
            }
          >
          <div ref={chartRef} >
              <ResponsiveContainer minWidth={500} height={viewTechnicians ? 1050 : 500}>

                   <LineChart
                   margin={{ top: 5, right: 30, left: 20, bottom: 100 }}
                     data={combinedData}
                     margin={{
                       top: 5,
                       right: 30,
                       left: 20,
                       bottom: 5,
                     }}
                   >
                     <CartesianGrid strokeDasharray="3 3" />
                     <XAxis dataKey="mon_year"/>
                     <YAxis yAxisId="left" orientation="left" { ...(dataView === 'Star Rate' ? { domain: [0, 5] } : {}) }
                            label={<CustomizedYaxisLabel name={leftYAxisLabelName} isLeft={true} chartWidth={chartWidth} labelY={viewTechnicians ? 400 : 50}/>}  />
                     <YAxis yAxisId="right" orientation="right"
                            domain={[0, 1]}
                            label={<CustomizedYaxisLabel name={rightYAxisLabelName} isLeft={false} chartWidth={chartWidth} labelY={viewTechnicians ? 400 : 50}/>} />
                     <Tooltip content={<CustomTooltip />} />
                     <Legend payload={legendItems} />
                     {viewTechnicians ?
                       technicianOptions.map((tech) => {
                         return [
                                <Line
                                  type="monotone"
                                  dataKey={dataView === 'Star Rate' ? `${tech.id}.star_rate` : (dataView === 'Tag Rate' ? `${tech.id}.tag_rate` : `${tech.id}.tag_count`)}
                                  stroke={warmColours[getTechnicianColorIndex(tech.id, true)]}
                                  yAxisId="left"
                                  name={`${tech.name} ${dataView}`}
                                />,
                                <Line
                                  type="monotone"
                                  dataKey={`${tech.id}.productivity_ratio`}
                                   stroke={coldColours[getTechnicianColorIndex(tech.id, false)]}
                                  yAxisId="right"
                                  name={`${tech.name} Productivity` }
                                />]
                         ;})

                         :

                       regionsOptions.map((region) => {
                       return [


                              <Line
                                type="monotone"
                                dataKey={dataView === 'Star Rate' ? `${region.id}.star_rate` : (dataView === 'Tag Rate' ? `${region.id}.tag_rate` : `${region.id}.tag_count`)}
                                stroke={regionWarmColours[region.id]}
                                yAxisId="left"
                                name={`${region.name} ${dataView}`}
                              />,
                              <Line
                                type="monotone"
                                dataKey={`${region.id}.productivity_ratio`}
                                stroke={regionColdColours[region.id]}
                                yAxisId="right"
                                name={`${region.name} Productivity` }
                              />]
                       ;})
                     }
                     {renderCompanyLine()}
                     {viewOption == 1 && regionalView !== 'NONE' && renderRegionalLine()}
                   </LineChart>


                </ResponsiveContainer>
           </div>
          </Widget>
          </Grid>
    </>
  );
}
